import { FC, useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Container, Box, Button, Card, CardContent, Grid, TextField, Breadcrumbs, Link, InputAdornment, IconButton, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Typography, Dialog, DialogContent, DialogContentText, DialogActions, TablePagination, Tooltip, DialogTitle } from "@material-ui/core";
import ReactDevicePreview from "react-device-preview";
import "react-phone-number-input/style.css";
import { Formik } from "formik";
import ChevronRightIcon from "src/icons/ChevronRight";
import { Link as RouterLink } from "react-router-dom";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import InputField from "src/components/ui-components/InputField";
import {
  SendNotificationMessage,
  getNotificationMessageLogList,
  deleteNotificationMessageLogData,
  GetConfigUsersList,
  GetNotificationRecipients,
} from "./ConnectionService";
import SelectField from "src/components/ui-components/SelectField";
import AlertMessage from "src/components/ui-components/AlertMessage";
import DeleteIcon from "@material-ui/icons/Delete";
import SendIcon from "@material-ui/icons/Send";
import * as Yup from "yup";
import moment from "moment";
import SearchIcon from '@material-ui/icons/Search';
import { Clear } from "@material-ui/icons";

const defaultForm = {
  username: "",
  title: "",
  body: "",
  messageData: "",
  image: "",
  schedule: "",
  isDraft: false,
  submit: null,
};

const SendNotification: FC = (props) => {
  const childRef = useRef(null);
  const [recipientList, setRecipientList] = useState<any[]>([]);
  const [notificationLogsList, setNotificationLogsList] = useState<any[]>([]);
  const isMountedRef = useRef(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [notificationId, setNotificationId] = useState("");
  const { email, configUserKey } = JSON.parse(localStorage.getItem("userDetails"));
  const [appKey, setAppKey] = useState<string>(null);
  const [draftPage, setDraftPage] = useState(0);
  const [draftRowsPerPage, setDraftRowsPerPage] = useState(5);
  const [logPage, setLogPage] = useState(0);
  const [logRowsPerPage, setLogRowsPerPage] = useState(5);

  const [draftSearchQuery, setDraftSearchQuery] = useState('');
  const [filteredDrafts, setFilteredDrafts] = useState(notificationLogsList);

  const [logSearchQuery, setLogSearchQuery] = useState('');
  const [filteredLogs, setFilteredLogs] = useState(notificationLogsList);

  const handleDraftQueryChange = (event) => {
    const query = event.target.value;
    setDraftSearchQuery(query);
    if (query) {
      const lowercasedQuery = query.toLowerCase();
      const filteredData = notificationLogsList.filter((log) =>
        (log.Recipient && log.Recipient.toLowerCase().includes(lowercasedQuery)) ||
        (log.MessageTitle && log.MessageTitle.toLowerCase().includes(lowercasedQuery)) ||
        (log.MessageBody && log.MessageBody.toLowerCase().includes(lowercasedQuery)) ||
        (log.Source && log.Source.toLowerCase().includes(lowercasedQuery))
      );
      setFilteredDrafts(filteredData);
    } else {
      setFilteredDrafts(notificationLogsList);
    }
  };

  const handleLogQueryChange = (event) => {
    const query = event.target.value;
    setLogSearchQuery(query);
    if (query) {
      const lowercasedQuery = query.toLowerCase();
      const filteredData = notificationLogsList.filter((log) =>
        (log.Recipient && log.Recipient.toLowerCase().includes(lowercasedQuery)) ||
        (log.MessageTitle && log.MessageTitle.toLowerCase().includes(lowercasedQuery)) ||
        (log.MessageBody && log.MessageBody.toLowerCase().includes(lowercasedQuery)) ||
        (log.Source && log.Source.toLowerCase().includes(lowercasedQuery))
      );
      setFilteredLogs(filteredData);
    } else {
      setFilteredLogs(notificationLogsList);
    }
  };

  const GetMobileUsersData = async () => {
    const user = await GetConfigUsersList(email);
    setAppKey(user[0].appKey);
    const res = await GetNotificationRecipients(user[0].appKey);
    setRecipientList(res);
  };
  const handleDelete = (id) => {
    setOpenDialog(true);
    setNotificationId(id);
  };

  const handleSendNotification = async (values, isDraft) => {
    const recipientData = recipientList.find((rec) => rec.RecipientName === values.username);
    const requestData = {
      notificationMessageLogKey: values.notificationMessageLogKey ?? null,
      username: recipientData.IsTopic ? null : values.username,
      topic: recipientData.IsTopic ? recipientData.RecipientName : null,
      appKey,
      title: values.title,
      body: values.body,
      messageData: values.messageData ?? null,
      isDraft,
      userKey: configUserKey,
    };
    try {
      const res = await SendNotificationMessage(requestData);
      if (res.isSuccess) {
        const message = isDraft
          ? "Notification saved as draft"
          : (!res.message || res.message.trim() === "" ? "Notification sent successfully" : res.message);
        AlertMessage({ type: "success", msg: message });
        GetNotificationMessageLogData();
      } else {
        AlertMessage({ type: "error", msg: isDraft ? "Failed to save notification" : "Failed to send notification" });
      }
      return res.isSuccess;
    } catch (error) {
      AlertMessage({ type: "error", msg: "Failed to send notification" });
      return false;
    }
  };

  const onDeleteNotificationMessageLog = async () => {
    await deleteNotificationMessageLogData(notificationId)
      .then(() => {
        setOpenDialog(false);
        AlertMessage({
          type: "success",
          msg: "Notification message deleted successfully",
        });
        GetNotificationMessageLogData();
      })
      .catch((err) => {
        AlertMessage({ type: "error", msg: err.title });
      });
    setOpenDialog(false);
  };

  const GetNotificationMessageLogData = async () => {
    if (appKey) {
      const res = await getNotificationMessageLogList(appKey, configUserKey);
      setNotificationLogsList(res);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      await GetMobileUsersData();
      GetNotificationMessageLogData();
    };
    initializeData();
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appKey]);

  const currentDate = new Date().toISOString().split("T")[0];

  const handleDraftPageChange = (event, newPage) => {
    setDraftPage(newPage);
  };

  const handleDraftRowsPerPageChange = (event) => {
    setDraftRowsPerPage(parseInt(event.target.value, 10));
    setDraftPage(0);
  };

  const handleLogPageChange = (event, newPage) => {
    setLogPage(newPage);
  };

  const handleLogRowsPerPageChange = (event) => {
    setLogRowsPerPage(parseInt(event.target.value, 10));
    setLogPage(0);
  };

  useEffect(() => {
    setFilteredDrafts(notificationLogsList);
    setFilteredLogs(notificationLogsList);
  }, [notificationLogsList]);

  return (
    <>
      <Helmet>
        <title>Notification</title>
      </Helmet>
      <Box sx={{ minHeight: "100vh", pb: 4 }}>
        <Container maxWidth={false}>
          <Grid alignItems="center" sx={{ my: 2 }} container justifyContent="space-between">
            <Grid item>
              <Breadcrumbs aria-label="breadcrumb" separator={<ChevronRightIcon fontSize="small" />}>
                <Link color="textPrimary" component={RouterLink} to="" variant="h5">
                  Administration
                </Link>
                <Typography color="textPrimary" variant="h5">
                  Notifications
                </Typography>
              </Breadcrumbs>
            </Grid>
          </Grid>
          <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete this notification?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDialog(false)} variant="outlined" color="primary">
                Cancel
              </Button>
              <Button onClick={onDeleteNotificationMessageLog} variant="contained" color="primary">
                Delete
              </Button>
            </DialogActions>
          </Dialog>
          <Box>
            <Grid item md={12} xs={12}>
              <Grid container spacing={5} >
                <Grid item md={12} xs={12}>
                  <Card sx={{ height: "100%" }}>
                    <Formik
                      enableReinitialize
                      innerRef={childRef}
                      initialValues={defaultForm}
                      validationSchema={
                        Yup.object().shape({
                          username: Yup.string()
                            .nullable()
                            .required("Recipient is required")
                            .max(100, "Recipient cannot be longer than 100 characters")
                            .test("Recipient", "Recipient cannot have leading or trailing spaces", (value) => !/^\s+|\s+$/.test(value)),
                          title: Yup.string()
                            .required("Notification Title is required")
                            .max(50, "Notification Title cannot be longer than 50 characters")
                            .test("title", "Notification Title cannot have leading or trailing spaces", (value) => !/^\s+|\s+$/.test(value)),
                          body: Yup.string()
                            .required("Notification Text is required")
                            .max(300, "Notification Text cannot be longer than 300 characters")
                            .test("body", "Notification Text cannot have leading or trailing spaces", (value) => !/^\s+|\s+$/.test(value)),
                          messageData: Yup.string(),
                        })
                      }
                      onSubmit={async (values, { resetForm }) => {
                        const result = await handleSendNotification(values, values.isDraft);
                        if (result) {
                          resetForm({ values: defaultForm });
                        }
                      }}
                    >
                      {({ handleSubmit, setFieldValue, values }): JSX.Element => {
                        return (
                          <>
                            <form
                              noValidate
                              onSubmit={handleSubmit}
                              {...props}
                              autoComplete="off"
                            >
                              <Grid
                                container
                                spacing={10}
                                sx={{ paddingTop: "2%" }}
                              >
                                <Grid item md={12} xs={12}>
                                  <CardContent>
                                    <Grid container spacing={3}>
                                      <Grid item md={7} xs={12}>
                                        <Grid container spacing={2}>
                                          <Grid item md={12} xs={12}>
                                            <Box>
                                              <SelectField
                                                data={recipientList}
                                                dataTextField={"Recipients"}
                                                dataValueField={"RecipientName"}
                                                name="username"
                                                label="Recipient *"
                                              />
                                            </Box>
                                          </Grid>
                                          <Grid item md={12} xs={12}>
                                            <Box>
                                              <InputField
                                                label="Notification Title *"
                                                name="title"
                                              />
                                            </Box>
                                          </Grid>
                                          <Grid item md={12} xs={12}>
                                            <Box>
                                              <InputField
                                                label="Notification Text *"
                                                name="body"
                                                multiline
                                                rows={4}
                                                fullWidth
                                              />
                                            </Box>
                                          </Grid>
                                          <Grid item md={12} xs={12}>
                                            <Box>
                                              <InputField
                                                label="Message Data"
                                                name="messageData"
                                                multiline
                                                rows={4}
                                                fullWidth
                                              />
                                            </Box>
                                          </Grid>
                                          <Grid item md={12} xs={12}>
                                            <Box>
                                              <TextField
                                                label="Image (Optional - TBD)"
                                                // name="image"
                                                disabled
                                                variant="outlined"
                                                fullWidth
                                                InputProps={{
                                                  endAdornment: (
                                                    <InputAdornment position="end">
                                                      <input
                                                        accept="image/*"
                                                        style={{
                                                          display: "none",
                                                        }}
                                                        id="upload-file"
                                                        type="file"
                                                      // onChange={
                                                      //   handleFileChange
                                                      // }
                                                      />
                                                      <label htmlFor="upload-file">
                                                        <IconButton
                                                          color="primary"
                                                          component="span"
                                                          disabled
                                                        >
                                                          <AttachFileIcon />
                                                        </IconButton>
                                                      </label>
                                                    </InputAdornment>
                                                  ),
                                                }}
                                              />
                                            </Box>
                                          </Grid>
                                          <Grid item md={12} xs={12}>
                                            <Box>
                                              <TextField
                                                // id="date"
                                                label="Schedule"
                                                type="date"
                                                disabled
                                                defaultValue={currentDate}
                                                fullWidth
                                                InputLabelProps={{
                                                  shrink: true,
                                                }}
                                              />
                                            </Box>
                                          </Grid>
                                        </Grid>
                                      </Grid>
                                      <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        md={6}
                                        lg={5}
                                        xl={4}
                                        style={{ margin: "auto" }}
                                      >
                                        <Box>
                                          <Box
                                            style={{
                                              textAlign: "left",
                                              marginBottom: "10px",
                                            }}
                                          >
                                            <Typography
                                              variant="h5"
                                              align="left"
                                              gutterBottom
                                            >
                                              Device Preview
                                            </Typography>
                                          </Box>

                                          <Box
                                            style={{
                                              position: "relative",
                                              overflow: "hidden",
                                              border: "1px solid #ccc",
                                              borderRadius: "8px",
                                              height: "350px",
                                              width: "100%",
                                            }}
                                          >
                                            <Box
                                              style={{
                                                position: "absolute",
                                                top: "0",
                                                left: "58%",
                                                transform: "translateX(-58%)",
                                                width: "80%",
                                                maxHeight: "50%",
                                                overflow: "hidden",
                                                paddingTop: "20px",
                                              }}
                                            >
                                              <ReactDevicePreview
                                                device="iphonex"
                                                scale="1"
                                              >
                                                <Box
                                                  style={{
                                                    position: "absolute",
                                                    top: "20px",
                                                    left: "20%",
                                                    transform: "translateX(-50%)",
                                                    background: "#fff",
                                                    paddingTop: "20px",
                                                  }}
                                                >
                                                  <h4
                                                    style={{ color: "#59595e" }}
                                                  >
                                                    Notification Title
                                                  </h4>
                                                  <h4
                                                    style={{ color: "#59595e" }}
                                                  >
                                                    Notification Text
                                                  </h4>
                                                </Box>
                                              </ReactDevicePreview>
                                            </Box>
                                          </Box>
                                        </Box>
                                      </Grid>
                                    </Grid>
                                    <Grid item>
                                      <Grid
                                        justifyContent="flex-end"
                                        container
                                        spacing={2}
                                        paddingTop={5}
                                      >
                                        <Grid item>
                                          <Button
                                            variant="contained"
                                            onClick={() => setFieldValue("isDraft", true)}
                                          >
                                            Save as draft
                                          </Button>
                                        </Grid>
                                        <Grid item>
                                          <Button
                                            variant="contained"
                                            onClick={() => setFieldValue("isDraft", false)}
                                          >
                                            Send
                                          </Button>
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                  </CardContent>
                                </Grid>
                              </Grid>
                            </form>
                          </>
                        );
                      }}
                    </Formik>
                  </Card>
                </Grid>
                <Grid item md={12} xs={12}>
                  <Card style={{ paddingBottom: '0' }}>
                    <CardContent style={{ paddingBottom: '0' }}>
                      <Typography variant="h5" align="left" gutterBottom>
                        Drafts
                      </Typography>
                      <Box sx={{ mb: 2, mt: 2, maxWidth: '100%', alignItems: 'center', display: 'flex', width: 300 }}>
                        <TextField
                          fullWidth
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                            ),
                            endAdornment: draftSearchQuery && (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => {
                                    setDraftSearchQuery('');
                                    setFilteredDrafts(notificationLogsList);
                                  }}
                                  edge="end"
                                  size="small"
                                >
                                  <Clear />
                                </IconButton>
                              </InputAdornment>
                            ),
                            style: { height: 43 },
                          }}
                          onChange={handleDraftQueryChange}
                          placeholder="Search Drafts"
                          value={draftSearchQuery}
                          variant="outlined"
                        />
                      </Box>
                      <TableContainer>
                        <Table style={{ tableLayout: 'fixed' }}>
                          <TableHead>
                            <TableRow>
                              <TableCell style={{ width: "20%" }}>Recipient</TableCell>
                              <TableCell style={{ width: "20%" }}>Notification Title</TableCell>
                              <TableCell style={{ width: "25%" }}>Notification Text</TableCell>
                              <TableCell style={{ width: "20%" }}>Source Name</TableCell>
                              <TableCell style={{ width: "15%" }}>Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredDrafts.some((log) => log.IsDraft === true) ? (
                              filteredDrafts
                                .filter((log) => log.IsDraft === true)
                                .slice(draftPage * draftRowsPerPage, draftPage * draftRowsPerPage + draftRowsPerPage)
                                .map((log, idx) => (
                                  <TableRow key={log.NotificationMessageLogKey ?? null}>
                                    <TableCell style={{ width: "20%" }}>
                                      <Tooltip title={log.Recipient} arrow>
                                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                                          {log.Recipient.includes('-') ? log.Recipient.split('-')[1] : log.Recipient}
                                        </div>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell style={{ width: "20%" }}>
                                      <Tooltip title={log.MessageTitle} arrow>
                                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                                          {log.MessageTitle}
                                        </div>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell style={{ width: "25%" }}>
                                      <Tooltip title={log.MessageBody} arrow>
                                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                                          {log.MessageBody}
                                        </div>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell style={{ width: "20%" }}>
                                      <Tooltip title={log.Source} arrow>
                                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                                          {log.Source}
                                        </div>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell style={{ width: "15%" }}>
                                      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
                                        <Tooltip title="Delete Notification">
                                          <IconButton
                                            aria-label="delete"
                                            size="small"
                                            sx={{ color: "#000" }}
                                            onClick={() => handleDelete(log.NotificationMessageLogKey)}
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Send Notification">
                                          <IconButton
                                            aria-label="send"
                                            size="small"
                                            sx={{ color: "#000" }}
                                            onClick={() => {
                                              handleSendNotification({
                                                username: log.Recipient,
                                                notificationMessageLogKey: log.NotificationMessageLogKey,
                                                title: log.MessageTitle,
                                                body: log.MessageBody,
                                                messageData: log.MessageData
                                              }, false)
                                            }}
                                          >
                                            <SendIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={5}>
                                  <Typography align="center" variant="subtitle1">
                                    No Data Found
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                        {notificationLogsList.some((log) => log.IsDraft === true) && (
                          <TablePagination
                            component="div"
                            count={notificationLogsList.filter((log) => log.IsDraft === true).length}
                            rowsPerPage={draftRowsPerPage}
                            page={draftPage}
                            onPageChange={handleDraftPageChange}
                            onRowsPerPageChange={handleDraftRowsPerPageChange}
                            rowsPerPageOptions={[5, 10, 25]}
                          />
                        )}
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item md={12} xs={12}>
                  <Card style={{ paddingBottom: '0' }}>
                    <CardContent style={{ paddingBottom: '0' }}>
                      <Typography variant="h5" align="left" gutterBottom>
                        Notifications Log
                      </Typography>
                      <Box sx={{ mb: 2, mt: 2, maxWidth: '100%', alignItems: 'center', display: 'flex', width: 300 }}>
                        <TextField
                          fullWidth
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                            ),
                            endAdornment: logSearchQuery && (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => {
                                    setLogSearchQuery('');
                                    setFilteredLogs(notificationLogsList);
                                  }}
                                  edge="end"
                                  size="small"
                                >
                                  <Clear />
                                </IconButton>
                              </InputAdornment>
                            ),
                            style: { height: 43 },
                          }}
                          onChange={handleLogQueryChange}
                          placeholder="Search Notifications"
                          value={logSearchQuery}
                          variant="outlined"
                        />
                      </Box>
                      <TableContainer>
                        <Table style={{ tableLayout: 'fixed' }}>
                          <TableHead>
                            <TableRow>
                              <TableCell style={{ flex: 1 }}>Recipient</TableCell>
                              <TableCell style={{ flex: 1 }}>Notification Title</TableCell>
                              <TableCell style={{ flex: 2 }}>Notification Text</TableCell>
                              <TableCell style={{ flex: 1 }}>Source Name</TableCell>
                              <TableCell style={{ flex: 1 }}>Sent On</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredLogs.length > 0 ? (
                              filteredLogs
                                .filter((log) => log.IsDraft === false)
                                .slice(logPage * logRowsPerPage, logPage * logRowsPerPage + logRowsPerPage)
                                .map((log, idx) => (
                                  <TableRow key={log.notificationMessageLogKey ?? idx}>
                                    <TableCell style={{ flex: 1 }}>
                                      <Tooltip title={log.Recipient} arrow>
                                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                                          {log.Recipient.includes('-') ? log.Recipient.split('-')[1] : log.Recipient}
                                        </div>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell style={{ flex: 1 }}>
                                      <Tooltip title={log.MessageTitle} arrow>
                                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                                          {log.MessageTitle}
                                        </div>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell style={{ flex: 2 }}>
                                      <Tooltip title={log.MessageBody} arrow>
                                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                                          {log.MessageBody}
                                        </div>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell style={{ flex: 1 }}>
                                      <Tooltip title={log.Source} arrow>
                                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                                          {log.Source}
                                        </div>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell style={{ flex: 1 }}>
                                      <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px' }}>
                                        {moment.utc(log.SentOn).local().format('DD-MMM-YYYY HH:mm')}
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={5}>
                                  <Typography align="center" variant="subtitle1">
                                    No Data Found
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                        {notificationLogsList.length > 0 && (
                          <TablePagination
                            component="div"
                            count={notificationLogsList.filter((log) => log.IsDraft === false).length}
                            rowsPerPage={logRowsPerPage}
                            page={logPage}
                            onPageChange={handleLogPageChange}
                            onRowsPerPageChange={handleLogRowsPerPageChange}
                            rowsPerPageOptions={[5, 10, 25]}
                          />
                        )}
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    </>
  )
};
export default SendNotification;
